import { useEffect, useState } from 'react';
import { api, ApiError, type User } from './api';
import { ErrorBoundary } from './components/ErrorBoundary';
import { KeyboardShortcutsModal } from './components/KeyboardShortcutsModal';
import { ConfirmHost } from './components/ConfirmHost';
import { ToastHost } from './components/ToastHost';
import { AcceptInvite } from './pages/AcceptInvite';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { parseEmbedParams } from './embed';

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; user: User };

// Lightweight path-based routing: only the invite-acceptance flow needs its
// own URL. Everything else just lives at /.
function detectInviteToken(): string | null {
  const m = window.location.pathname.match(/^\/setup\/([^/?#]+)\/?$/);
  return m ? m[1]! : null;
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });
  const [inviteToken] = useState<string | null>(detectInviteToken());
  const [embed] = useState(() => parseEmbedParams(window.location.search));
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Global Cmd+/ (Ctrl+/ on non-Mac) and a custom 'app:open-shortcuts'
  // event so the Help section can also open it. Lives on the App root so
  // it's available regardless of which page is mounted.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    function onOpen() {
      setShortcutsOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('app:open-shortcuts', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('app:open-shortcuts', onOpen);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        sessionStorage.removeItem('pc3_sso_attempted');
        setAuth({ status: 'signed-in', user: res.user });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setAuth({ status: 'signed-out' });
        } else {
          // Treat unexpected errors as signed-out so the user can attempt to log in.
          // eslint-disable-next-line no-console
          console.error('me check failed', err);
          setAuth({ status: 'signed-out' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // PC-3 embed-mode auto-SSO: when signed-out inside the platform iframe, start the OIDC
  // SSO silently (the user is already signed into the platform provider). A sessionStorage
  // sentinel prevents an infinite redirect loop if the session cookie is blocked
  // (third-party-cookie / ITP): a second signed-out arrival renders the login fallback
  // instead of re-redirecting. The sentinel is cleared on a successful api.me() above.
  useEffect(() => {
    if (auth.status !== 'signed-out' || !embed.embed) return;
    if (sessionStorage.getItem('pc3_sso_attempted') === '1') return;
    sessionStorage.setItem('pc3_sso_attempted', '1');
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = '/api/auth/oidc/start?return_to=' + encodeURIComponent(returnTo);
  }, [auth.status, embed.embed]);

  if (auth.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Loading…</div>
      </div>
    );
  }

  // /setup/<token> path takes priority — even signed-in users see the accept
  // flow if they navigate there (rare, but handle it cleanly).
  if (inviteToken && auth.status !== 'signed-in') {
    return (
      <AcceptInvite
        token={inviteToken}
        onAccepted={(user) => {
          // Replace the URL so a refresh doesn't re-trigger the accept flow.
          window.history.replaceState({}, '', '/');
          setAuth({ status: 'signed-in', user });
        }}
      />
    );
  }

  if (auth.status === 'signed-out') {
    // Embed mode: the auto-SSO effect above navigates to the OIDC start - show a placeholder
    // while that happens. If we already attempted SSO and returned still signed-out (the
    // session cookie was blocked), the sentinel is set, so we render the login fallback
    // instead of looping.
    if (embed.embed && sessionStorage.getItem('pc3_sso_attempted') !== '1') {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-zinc-500 text-sm">Signing in…</div>
        </div>
      );
    }
    return <Login onLogin={(user) => setAuth({ status: 'signed-in', user })} />;
  }

  // Top-level boundary so a render crash in any descendant shows a recovery
  // surface instead of a blank page. Sub-trees that mount expensive modals
  // (Settings, CallModal) wrap themselves with their own boundary so a
  // crash in the modal doesn't take the chat surface down with it.
  // ToastHost sits outside the boundary so it stays alive even if the app
  // tree crashes — a recovery toast can fire from anywhere.
  return (
    <>
      <ErrorBoundary label="the app">
        <Home
          user={auth.user}
          onLogout={() => setAuth({ status: 'signed-out' })}
          initialConversationId={embed.conversation}
        />
      </ErrorBoundary>
      <ToastHost />
      <ConfirmHost />
      {shortcutsOpen && (
        <KeyboardShortcutsModal onClose={() => setShortcutsOpen(false)} />
      )}
    </>
  );
}
